import React, { useState } from 'react';
import styles from '../styles/components/SignUpModal.module.css';
import formStyles from '../styles/components/form/Forms.module.css';
import Modal from '@ax2/common/components/Modal';
import { Formik } from 'formik-v2';
import * as Yup from 'yup';
import TextField from './form/TextField';
import { ActionButton } from '@ax2/common/components/ActionButton';
import { RestError, signIn, signUp } from '../rest/authClient';
import SignInTO from '../model/SignInTO';
import { useUserUpdateContext } from '../contexts/UserContext';
import SignUpTO from '../model/SignUpTO';
import PasswordField from './form/PasswordField';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { getErrorMessage } from '@ax2/common/helpers/error';
import {
  firstNameSchema,
  lastNameSchema,
  companyNameSchema,
  companyRoleSchema,
  emailSchema,
  passwordSchema,
  repeatPasswordSchema,
} from 'helpers/validations';
import TrialExpiredModal from './TrialExpiredModal';

type SignUpModalAction = 'signin' | 'signup' | 'forgotPassword';

interface SignUpProps {
  switchToSignIn: () => void;
  topText?: string;
  buttonText?: string;
  onSuccess?: () => void;
}

interface SignUpFormValues {
  firstName: string;
  lastName: string;
  companyName: string;
  companyRole: string;
  email: string;
  password: string;
  repeatPassword: string;
}

const signUpSchema = Yup.object<SignUpFormValues>().shape({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  companyName: companyNameSchema,
  companyRole: companyRoleSchema,
  email: emailSchema,
  password: passwordSchema,
  repeatPassword: repeatPasswordSchema,
});

const SignUp: React.FC<SignUpProps> = ({
  switchToSignIn,
  topText,
  buttonText = 'Create account',
  onSuccess,
}) => {
  const updateUser = useUserUpdateContext();
  const [formError, setFormError] = useState<string | null>(null);
  const initialValues: SignUpFormValues = {
    firstName: '',
    lastName: '',
    companyName: '',
    companyRole: '',
    email: '',
    password: '',
    repeatPassword: '',
  };
  return (
    <div className={styles.signUp}>
      <div className={styles.textWrapper}>
        {topText ? <p className={styles.text}>{topText}</p> : null}
        <p className={[styles.text, styles.mutedText].join(' ')}>
          If you already have an account,{' '}
          <span className={styles.link} onClick={switchToSignIn}>
            click here to log in.
          </span>
        </p>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={signUpSchema}
        onSubmit={(values, { setSubmitting }) => {
          signUp(
            new SignUpTO(
              values.email,
              values.password,
              values.firstName.trim(),
              values.lastName.trim(),
              values.companyName,
              values.companyRole
            )
          )
            .then(authResponse => {
              setSubmitting(false);
              updateUser(authResponse);
              onSuccess && onSuccess();
            })
            .catch((err: RestError) => {
              setSubmitting(false);
              setFormError(err.description);
            });
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit} className={formStyles.form}>
            <div className={formStyles.formItems}>
              <div
                className={[
                  formStyles.formRow,
                  formStyles.formItemFullWidth,
                ].join(' ')}
              >
                <TextField
                  name="firstName"
                  label="First Name"
                  labelNote="required"
                  wrapperClassName={formStyles.formRowCell}
                />
                <TextField
                  name="lastName"
                  label="Last Name"
                  labelNote="required"
                  wrapperClassName={formStyles.formRowCell}
                />
              </div>
              <div
                className={[
                  formStyles.formRow,
                  formStyles.formItemFullWidth,
                ].join(' ')}
              >
                <TextField
                  name="companyName"
                  label="Company Name"
                  labelNote="required"
                  wrapperClassName={formStyles.formRowCell}
                />
                <TextField
                  name="companyRole"
                  label="Company Role"
                  labelNote="required"
                  wrapperClassName={formStyles.fromRowCell}
                />
              </div>
              <TextField
                name="email"
                label="Company Email"
                labelNote="required"
                wrapperClassName={formStyles.formItemShorterWidth}
              />
              <PasswordField
                name="password"
                label="Password"
                labelNote="required"
                wrapperClassName={formStyles.formItemShorterWidth}
              />
              <PasswordField
                name="repeatPassword"
                label="Repeat Password"
                labelNote="required"
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onKeyPress={(e: any) => {
                  e.key === 'Enter' && !isSubmitting && handleSubmit();
                }}
                wrapperClassName={formStyles.formItemShorterWidth}
              />
              {formError ? (
                <div className={formStyles.formError}>{formError}</div>
              ) : null}
              <div className={formStyles.formButtons}>
                <ActionButton
                  onClick={() => !isSubmitting && handleSubmit()}
                  loading={isSubmitting}
                  type="primary"
                  text={buttonText}
                />
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

interface SignInProps {
  switchToSignUp: () => void;
  switchToForgotPassword: () => void;
  topText?: string;
  buttonText?: string;
  onSuccess?: () => void;
}

interface SignInFormValues {
  email: string;
  password: string;
}

const signInSchema = Yup.object<SignInFormValues>().shape({
  email: emailSchema,
  password: passwordSchema,
});

const SignIn: React.FC<SignInProps> = ({
  switchToSignUp,
  switchToForgotPassword,
  topText,
  buttonText,
  onSuccess,
}) => {
  const updateUser = useUserUpdateContext();
  const [formError, setFormError] = useState<string | null>(null);
  const [isTrialExpired, setTrialExpired] = useState(false);

  const initialValues: SignInFormValues = {
    email: '',
    password: '',
  };
  return (
    <div className={styles.signUp}>
      {isTrialExpired && (
        <TrialExpiredModal
          onClose={() => {
            setTrialExpired(false);
          }}
        />
      )}
      <div className={styles.textWrapper}>
        {topText ? <p className={styles.text}>{topText}</p> : null}
        <p className={[styles.text, styles.mutedText].join(' ')}>
          If you don&apos;t have an account,{' '}
          <span className={styles.link} onClick={switchToSignUp}>
            click here to sign up.
          </span>
        </p>
        <p className={[styles.text, styles.mutedText].join(' ')}>
          Forgot your password?{' '}
          <span className={styles.link} onClick={switchToForgotPassword}>
            click here to reset.
          </span>
        </p>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={signInSchema}
        onSubmit={(values, { setSubmitting }) => {
          signIn(new SignInTO(values.email, values.password))
            .then(authResponse => {
              setSubmitting(false);
              updateUser(authResponse);
              onSuccess && onSuccess();
            })
            .catch((err: RestError) => {
              setSubmitting(false);
              if (err.reason === 'AccountExpired') {
                setTrialExpired(true);
              } else {
                setFormError(getErrorMessage(err));
              }
            });
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit} className={formStyles.form}>
            <div className={formStyles.formItems}>
              <TextField
                name="email"
                label="Email"
                labelNote="required"
                wrapperClassName={formStyles.formItemShorterWidth}
              />
              <TextField
                name="password"
                label="Password"
                labelNote="required"
                type="password"
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onKeyPress={(e: any) => {
                  e.key === 'Enter' && !isSubmitting && handleSubmit();
                }}
                wrapperClassName={formStyles.formItemShorterWidth}
              />
              {formError ? (
                <div className={formStyles.formError}>{formError}</div>
              ) : null}
              <div className={formStyles.formButtons}>
                <ActionButton
                  onClick={() => !isSubmitting && handleSubmit()}
                  loading={isSubmitting}
                  type="primary"
                  text={buttonText}
                />
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

interface ForgotPasswordProps {
  switchToSignIn: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ switchToSignIn }) => {
  return (
    <div className={styles.forgotPassword}>
      <div className={styles.textWrapper}>
        <p className={[styles.text, styles.mutedText].join(' ')}>
          If you remember your password,{' '}
          <span className={styles.link} onClick={switchToSignIn}>
            click here to sign in.
          </span>
        </p>
      </div>
      <ForgotPasswordForm />
    </div>
  );
};

export interface SignUpModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  defaultAction?: SignUpModalAction;
  signUpTopText?: string;
  signUpButtonText?: string;
  signInTopText?: string;
  signInButtonText?: string;
  onSuccess?: () => void;
}

const SignUpModal: React.FC<SignUpModalProps> = ({
  open,
  setOpen,
  defaultAction = 'signup',
  signUpTopText,
  signUpButtonText,
  signInTopText,
  signInButtonText,
  onSuccess,
}) => {
  const [action, setAction] = useState<SignUpModalAction>(defaultAction);

  if (!open) {
    return null;
  }

  return (
    <Modal onClose={() => setOpen(false)}>
      <div className={styles.modalWrapper}>
        <div className={styles.modalContents}>
          {action === 'signup' ? (
            <SignUp
              onSuccess={onSuccess}
              switchToSignIn={() => setAction('signin')}
              topText={signUpTopText}
              buttonText={signUpButtonText}
            />
          ) : action === 'signin' ? (
            <SignIn
              onSuccess={onSuccess}
              switchToSignUp={() => setAction('signup')}
              switchToForgotPassword={() => setAction('forgotPassword')}
              topText={signInTopText}
              buttonText={signInButtonText}
            />
          ) : (
            <ForgotPassword switchToSignIn={() => setAction('signin')} />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SignUpModal;
